"use client";
// components/WishlistSummaryCard.jsx
import PriceHistogram from "./PriceHistogram";
import { useEffect, useState, useRef } from "react";

export default function WishlistSummaryCard({ schoolID, itemMinPrice, itemMaxPrice, categories, onViewDetail }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!schoolID) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    // NOTE: don't clear data — keep old values visible while refetching

    const params = new URLSearchParams();
    if (itemMinPrice) params.set("itemMinPrice", itemMinPrice);
    if (itemMaxPrice) params.set("itemMaxPrice", itemMaxPrice);
    if (categories?.length) params.set("categories", categories.join(","));

    fetch(`/api/wishlist/${schoolID}/summary?${params.toString()}`, { signal: controller.signal })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { if (e.name !== "AbortError") { setError(e.message); setLoading(false); } });

    return () => controller.abort();
  }, [schoolID, itemMinPrice, itemMaxPrice, categories]);

  // Initial load
  if (!data && loading) return (
    <div style={s.card}>
      {[60, 40, 100, 60].map((w, i) => (
        <div key={i} style={{ ...s.shimmer, width: `${w}%`, height: 12, marginBottom: 8 }} />
      ))}
    </div>
  );

  if (error || !data) return (
    <div style={s.card}>
      <p style={{ color: "#f87171", fontSize: 12 }}>Could not load wishlist.</p>
    </div>
  );

  const { stats, itemFilterActive } = data;
  const displayItems = stats.totalItems;
  const displayCost  = stats.totalCost;
  const displayLabel = "Total Cost";
  const itemsLabel   = "Total Items";
  return (
    <div style={{ ...s.card, position: "relative" }}>
      {/* Subtle updating indicator — doesn't blank the card */}
      {loading && (
        <div style={{
          position: "absolute", top: 8, right: 8,
          fontSize: 10, color: "#f97316", fontWeight: 700,
          background: "#fff7ed", padding: "2px 8px", borderRadius: 999,
          border: "1px solid #fed7aa",
        }}>
          Updating...
        </div>
      )}

      <div style={s.header}>
        <div style={s.avatar}>{data.teacherName?.charAt(0) || "T"}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.teacherName}>{data.teacherName}</div>
          <div style={s.schoolName}>{data.schoolName}</div>
          <div style={s.location}>{data.city}, {data.state} · Grade {data.grade}</div>
        </div>
      </div>

      <div style={s.statsRow}>
        <div style={s.statBox}>
          <div style={{ ...s.statNum, color: "#f97316" }}>{displayItems}</div>
          <div style={s.statLabel}>{itemsLabel}</div>
        </div>
        <div style={s.divider} />
        <div style={s.statBox}>
          <div style={{ ...s.statNum, color: "#10b981" }}>
            ${(displayCost || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
          <div style={s.statLabel}>{displayLabel}</div>
        </div>
      </div>

      <PriceHistogram
        histogram={data.histogram}
        itemMinPrice={itemMinPrice}
        itemMaxPrice={itemMaxPrice}
        itemFilterActive={itemFilterActive}
      />
    </div>
  );
}

const s = {
  card:        { fontFamily: "'Segoe UI',system-ui,sans-serif", width: 280, padding: 16, background: "#fff", borderRadius: 16 },
  shimmer:     { background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", borderRadius: 6 },
  header:      { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 },
  avatar:      { width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#f97316,#fbbf24)", color: "#fff", fontWeight: 800, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  teacherName: { fontWeight: 700, fontSize: 14, color: "#1e293b", lineHeight: 1.2 },
  schoolName:  { fontSize: 12, color: "#475569", marginTop: 2 },
  location:    { fontSize: 11, color: "#94a3b8", marginTop: 1 },
  statsRow:    { display: "flex", alignItems: "center", justifyContent: "space-around", background: "#f8fafc", borderRadius: 12, padding: "10px 8px", marginBottom: 12 },
  statBox:     { textAlign: "center" },
  statNum:     { fontSize: 20, fontWeight: 800, lineHeight: 1 },
  statLabel:   { fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 2 },
  divider:     { width: 1, height: 32, background: "#e2e8f0" },
};